import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader, Play, RefreshCw, SkipForward } from "lucide-react";
import { useApi } from "../hooks/useApi";

/**
 * Operator diagnostic for the behavioural feeds (§8.7).
 *
 * The module was consistently honest about what it did not know — coverage rows, "cannot say
 * whether it is in use" — and consistently unable to say *why*. Every question a verification pass
 * actually asks (when did this last work, did that run read anything, is coverage growing) needed a
 * query against the regional database. This answers them in place.
 *
 * Only feeds that run outside the discovery scan appear as runnable. The directory-audit and
 * AWS-role-usage feeds arrive with "Scan Now", and a second button for the same work would only
 * invite the two paths to drift.
 */
const FEED_LABEL = {
  "entra-signin": "Entra sign-in",
  "aws-access-advisor": "AWS Access Advisor",
  "gcp-policy-analyzer": "Google Cloud IAM activity",
  "estate-notifications": "Estate change email",
  "provider-usage": "Provider usage & cost",
  "runtime-actions": "Runtime actions",
  "anthropic-activity": "Anthropic activity",
};

const FEED_DESCRIPTION = {
  "entra-signin": "Advances the observation window dormancy findings are measured against.",
  "aws-access-advisor": "Adds per-service usage detail, which is what unused-permission findings rest on.",
  "gcp-policy-analyzer": "Adds Google's IAM policy insights, which unused-role findings rest on (requires Security Command Center Premium or Enterprise).",
  "estate-notifications": "Delivers urgent alerts and the daily digest. Rehearses by default.",
  "provider-usage": "Reads daily tokens and billed cost. Backfills 90 days on its first run.",
  "runtime-actions": "Records the kinds of Microsoft Graph call each agent was observed making, and flags the first time an agent makes a new kind of call.",
  "anthropic-activity": "Reads the Anthropic Compliance API activity feed and records the activity observed: identifiers and timing, never prompt, response or tool text.",
};

// The Anthropic activity feed shows the declared start and the observed window instead of the
// generic day-count label: before the field audit it may describe what was read, never what is
// covered (PLAN.md D6).
export const ANTHROPIC_ACTIVITY_FEED = "anthropic-activity";

/**
 * Every feed status the server writes (AiBehavioralFeedStatuses), each with its own meaning.
 * `Unverified` is its own state: the read succeeded and the feed has not yet been field-audited.
 * It is neither a warning nor a failure. A status missing from this map renders neutral, never as
 * healthy or failed.
 */
export const STATUS_META = {
  Healthy: { kind: "healthy", tone: "text-green-600", detail: null },
  Unverified: { kind: "unverified", tone: "text-blue-600", detail: "read succeeded; not yet field-audited" },
  Failed: { kind: "failure", tone: "text-red-600", detail: null },
  Unauthorized: { kind: "failure", tone: "text-red-600", detail: null },
  NotConfigured: { kind: "inactive", tone: "text-content-secondary", detail: null },
};

export const UNKNOWN_STATUS_META = { kind: "unknown", tone: "text-content-secondary", detail: null };

export const statusMeta = (status) =>
  (Object.prototype.hasOwnProperty.call(STATUS_META, status) ? STATUS_META[status] : UNKNOWN_STATUS_META);

const GAP_REASON = {
  ReadFailedWindow: "a read failed, so this span has not been read",
  DeclaredStartAfterObserved: "activity was observed before the declared enablement date, so the declared date may be later than the real one",
};

const OUTCOME_ICON = {
  Succeeded: <CheckCircle2 className="w-3 h-3 text-green-600" />,
  Failed: <AlertTriangle className="w-3 h-3 text-red-600" />,
  Skipped: <SkipForward className="w-3 h-3 text-content-secondary" />,
  LockHeld: <Loader className="w-3 h-3 text-content-secondary" />,
};

function ago(value) {
  if (!value) return "never";
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function days(value) {
  return value === null || value === undefined ? null : `${value.toFixed(1)}d`;
}

function formatDay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/**
 * The observed window with every gap cut out of it. A gap (open gaps run to the present) is never
 * bridged: the window is returned as the separate spans either side of it, so nothing on screen
 * reads as continuous across a hole. Gaps from every connector are cut, because the window is
 * organization-level and must not claim one connector's reach for another.
 */
export function observedSpans(start, end, gaps = []) {
  if (!start || !end) return [];
  const a0 = new Date(start).getTime();
  const b0 = new Date(end).getTime();
  if (Number.isNaN(a0) || Number.isNaN(b0) || b0 < a0) return [];
  let spans = [[a0, b0]];
  for (const gap of gaps) {
    const gs = new Date(gap.gapStart).getTime();
    const ge = gap.gapEnd ? new Date(gap.gapEnd).getTime() : Infinity;
    if (Number.isNaN(gs)) continue;
    spans = spans.flatMap(([a, b]) => {
      if (ge <= a || gs >= b) return [[a, b]];
      const out = [];
      if (gs > a) out.push([a, gs]);
      if (ge < b) out.push([ge, b]);
      return out;
    });
  }
  return spans.map(([a, b]) => ({ start: new Date(a).toISOString(), end: new Date(b).toISOString() }));
}

/**
 * What the generic row cannot know about the Anthropic activity feed: per connector, the declared
 * enablement date, the time its position last advanced and its gaps; and the observed window as
 * its own figure, beside the declared dates and never reconciled with them.
 */
function AnthropicActivityDetail({ status, diagnostics, grcConnectors }) {
  const connectors = diagnostics?.connectors || [];
  const allGaps = connectors.flatMap((c) => c.gaps || []);
  const spans = observedSpans(status?.coverageStart, status?.coverageEnd, allGaps);

  return (
    <div data-testid="anthropic-activity-detail" className="mt-2 space-y-2 text-[11px] text-content-secondary">
      <div data-testid="anthropic-activity-observed">
        <span className="text-content-primary">Observed</span>
        {spans.length === 0
          ? <span> · no activity observed yet</span>
          : spans.map((s) => (
            <span key={s.start} data-testid="observed-span">
              {" · "}activity observed {formatDay(s.start)} to {formatDay(s.end)}
            </span>
          ))}
      </div>
      {diagnostics?.readFailed && (
        <p data-testid="anthropic-activity-diagnostics-failed">
          The connector positions and gaps could not be read just now.
        </p>
      )}
      {connectors.map((c) => {
        const connector = (grcConnectors || []).find((g) => g.id === c.grcConnectorId);
        const declared = connector?.anthropicComplianceEnabledAt;
        const gaps = c.gaps || [];
        return (
          <div key={c.grcConnectorId} data-testid={`anthropic-activity-connector-${c.grcConnectorId}`} className="pl-2 border-l border-border">
            <div className="text-content-primary">
              {c.displayName || connector?.displayName || `Anthropic connector ${c.grcConnectorId}`}
            </div>
            <div className="flex flex-wrap gap-x-4">
              <span data-testid="declared-start">
                Declared · Compliance API enabled on {declared ? formatDay(declared) : "(not declared)"}
              </span>
              <span data-testid="cursor-advanced">Position last advanced {ago(c.cursorLastAdvancedAt)}</span>
            </div>
            {gaps.map((g) => (
              <div key={g.id ?? g.gapStart} data-testid="feed-gap" className="text-amber-700">
                Gap · {formatDay(g.gapStart)} to {g.gapEnd ? formatDay(g.gapEnd) : "now (still open)"}
                {" · "}{GAP_REASON[g.reason] ?? g.reason}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** One line per run. Which numbers are worth showing depends on what the feed counts. */
function RunLine({ run }) {
  const isAnthropicActivity = run.feedKey === ANTHROPIC_ACTIVITY_FEED;
  const gained = run.coverageDaysGained;
  const parts = [];
  if (run.rowsScanned !== null && run.rowsScanned !== undefined) {
    parts.push(`${run.rowsScanned} scanned`);
  }
  if (run.pagesRead) parts.push(`${run.pagesRead} page${run.pagesRead === 1 ? "" : "s"}`);
  if (run.itemsRecorded !== null && run.itemsRecorded !== undefined) {
    parts.push(`${run.itemsRecorded} recorded`);
  }
  // Runtime actions: calls by principals that match no tracked agent are counted as coverage,
  // never dropped silently, so the operator can see how much of the read was not attributed.
  // The Anthropic activity feed counts its unattributable records separately (below); its
  // UnattributedCount is a subset of them, and its "calls" wording belongs to runtime actions.
  if (!isAnthropicActivity && run.unattributedCount !== null && run.unattributedCount !== undefined) {
    parts.push(`${run.unattributedCount.toLocaleString("en-US")} calls not attributed to a tracked agent`);
  }
  // So a low recorded figure is explicable: Axaris's own reads appear in this feed.
  if (run.selfExcludedCount !== null && run.selfExcludedCount !== undefined) {
    parts.push(`${run.selfExcludedCount.toLocaleString("en-US")} of Axaris's own polling removed from the figures`);
  }
  if (run.unresolvableActorCount !== null && run.unresolvableActorCount !== undefined) {
    parts.push(`${run.unresolvableActorCount.toLocaleString("en-US")} seen but not attributable to any workspace or person`);
  }
  // Coverage is the number a verification pass is actually chasing, so it is stated as a change
  // rather than a level — "28.4d" does not say whether the last run helped.
  if (!isAnthropicActivity && gained !== null && gained !== undefined && Math.abs(gained) >= 0.05) {
    parts.push(`coverage ${gained > 0 ? "+" : ""}${gained.toFixed(1)}d → ${days(run.coverageDaysAfter)}`);
  }

  return (
    <div className="flex items-start gap-2 py-1 text-[11px]">
      <span className="mt-0.5 shrink-0">{OUTCOME_ICON[run.outcome] ?? null}</span>
      <span className="text-content-secondary shrink-0 w-16">{ago(run.startedAt)}</span>
      <span className="text-content-secondary shrink-0 w-14">
        {run.triggerSource === "Manual" ? "manual" : "scheduled"}
      </span>
      <span className="text-content-primary break-words">
        {run.failureReason || run.skipReason || run.summary || run.outcome}
        {parts.length > 0 && (
          <span className="text-content-secondary"> · {parts.join(" · ")}</span>
        )}
        {run.dryRun && <span className="text-content-secondary"> · rehearsal, nothing sent</span>}
        {/* A failed run can still carry a summary worth reading (a truncated runtime-actions read
            names the unread span there), so the failure reason must not hide it. */}
        {run.failureReason && run.summary && (
          <span data-testid="run-summary" className="block text-content-secondary">{run.summary}</span>
        )}
      </span>
    </div>
  );
}

export default function AiFeedOperations({ canManage, grcConnectors = [] }) {
  const apiCall = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const result = await apiCall("GET", "/ai-assets/feeds");
      setData(result);
      setError(null);
    } catch (e) {
      // Rendering nothing on failure makes a broken endpoint indistinguishable from a panel with
      // nothing to say — the exact ambiguity this panel exists to remove.
      setData(null);
      setError(e?.message || "could not be loaded");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (feedKey, send) => {
    setRunning(feedKey);
    setNotice(null);
    try {
      const query = send ? "?send=true" : "";
      const result = await apiCall("POST", `/ai-assets/feeds/${feedKey}/run${query}`);
      setNotice({ feedKey, message: result.message || result.outcome, tone: "ok" });
      await load();
    } catch (e) {
      // A 409 is the ordinary answer when the schedule is mid-cycle, not a fault.
      setNotice({
        feedKey,
        message: e?.response?.data?.message || e?.message || "The run could not be started.",
        tone: "warn",
      });
    } finally {
      setRunning(null);
    }
  };

  if (loading) return null;

  if (error || !data) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-content-secondary">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-semibold text-content-primary">Feed status unavailable</span>
        <span className="text-xs">{error ?? "no response"}</span>
      </div>
    );
  }

  // The feed→source mapping comes from the server, which already owns it. A second copy here
  // would be a second thing to get wrong, and the wrong answer looks exactly like a feed that has
  // never run.
  const statusFor = (sourceSystem) =>
    sourceSystem ? (data.feeds || []).find((f) => f.sourceSystem === sourceSystem) : null;

  return (
    <div className="py-2">
      {(data.runnable || []).map(({ key: feedKey, sourceSystem }) => {
        const status = statusFor(sourceSystem);
        const runs = (data.runs || []).filter((r) => r.feedKey === feedKey).slice(0, 5);
        const isNotification = feedKey === "estate-notifications";
        const isAnthropicActivity = feedKey === ANTHROPIC_ACTIVITY_FEED;
        const meta = status ? statusMeta(status.status) : null;
        // Delivery feeds keep no coverage window, so they have no status row. Falling back to the
        // run history stops them reading "never" directly above a list of runs that plainly
        // happened — which looks like the panel is broken rather than like a feed without coverage.
        // A rehearsal is not a success: it deliberately did not deliver.
        const lastSuccess = runs.find((r) => r.outcome === "Succeeded" && !r.dryRun)?.startedAt;

        return (
          <div key={feedKey} data-testid={`feed-row-${feedKey}`} className="py-3 border-b border-border last:border-b-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-content-primary">
                    {FEED_LABEL[feedKey] ?? feedKey}
                  </span>
                  {status && (
                    <span data-testid={`feed-status-${feedKey}`} data-status-kind={meta.kind} className={`text-[11px] ${meta.tone}`}>
                      {status.status}
                      {meta.detail && <span className="text-content-secondary"> · {meta.detail}</span>}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-content-secondary mt-0.5">
                  {FEED_DESCRIPTION[feedKey]}
                </p>
                <div className="text-[11px] text-content-secondary mt-1 flex flex-wrap gap-x-4">
                  <span>Last attempt {ago(status?.lastPolledAt ?? runs[0]?.startedAt)}</span>
                  {/* The distinction the column was added for: a feed that broke last week and one
                      that ran a minute ago used to read identically. */}
                  <span>Last success {ago(status?.lastSucceededAt ?? lastSuccess)}</span>
                  {!isAnthropicActivity && status?.coverageDays !== undefined && status?.coverageDays !== null && (
                    <span>Coverage {days(status.coverageDays)}</span>
                  )}
                </div>
                {status?.message && (
                  <p className="text-[11px] text-content-secondary mt-1">{status.message}</p>
                )}
                {isAnthropicActivity && (
                  <AnthropicActivityDetail
                    status={status}
                    diagnostics={data.anthropicActivity}
                    grcConnectors={grcConnectors}
                  />
                )}
              </div>

              {canManage && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => run(feedKey, false)}
                    disabled={running !== null}
                    className="px-2 py-1 rounded-md border border-border text-xs text-content-secondary hover:bg-theme-hover disabled:opacity-60 flex items-center gap-1"
                  >
                    {running === feedKey
                      ? <Loader className="w-3 h-3 animate-spin" />
                      : <Play className="w-3 h-3" />}
                    {isNotification ? "Rehearse" : "Run now"}
                  </button>
                  {/* Sending is a second, deliberate click. This is the one path in the module that
                      reaches people outside it. */}
                  {isNotification && (
                    <button
                      onClick={() => run(feedKey, true)}
                      disabled={running !== null}
                      className="px-2 py-1 rounded-md border border-border text-xs text-content-secondary hover:bg-theme-hover disabled:opacity-60"
                    >
                      Send for real
                    </button>
                  )}
                </div>
              )}
            </div>

            {notice?.feedKey === feedKey && (
              <p className={`text-[11px] mt-2 ${
                notice.tone === "warn" ? "text-amber-600" : "text-content-primary"
              }`}>
                {notice.message}
              </p>
            )}

            {runs.length > 0 && (
              <div className="mt-2 pl-1">{runs.map((r) => <RunLine key={r.id} run={r} />)}</div>
            )}
            {runs.length === 0 && (
              <p className="text-[11px] text-content-secondary mt-2">
                No runs recorded yet. History starts from the first run after this was deployed.
              </p>
            )}
          </div>
        );
      })}

      <button
        onClick={load}
        className="mt-2 text-[11px] text-content-secondary hover:text-content-primary flex items-center gap-1"
      >
        <RefreshCw className="w-3 h-3" /> Refresh
      </button>
    </div>
  );
}
